import React, { useEffect, useState } from "react";
import {
  FaHistory,
  FaFilter,
  FaSearch,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaInfoCircle,
  FaMobileAlt,
  FaUniversity,
  FaQuestionCircle,
  FaBroom,
  FaChevronDown,
  FaChevronUp,
  FaDownload,
  FaEye,
  FaUndo,
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface PaymentAuditDto {
  id: number;
  purpose: "INSTALLMENT" | "APPOINTMENT" | "COURSE";
  amount: number;
  paymentMethod: string;
  paymentId?: number;
  transactionId?: string;
  bankName?: string;
  branchName?: string;
  referenceNumber?: string;
  paymentDate?: string;
  submittedAt?: string;
  studentId: number;
  studentName: string;
  studentEmail: string;
  installmentId?: number;
  installmentSequence?: number;
  courseName?: string;
  batchName?: string;
  decision: "APPROVED" | "REJECTED";
  decisionReason?: string;
  decidedAt: string;
  verifierId: number;
  verifierName: string;
  verifierEmail: string;
  verifierRole: string;
}

interface PaymentHistoryResponse {
  content: PaymentAuditDto[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  first: boolean;
  last: boolean;
}

interface FilterState {
  purpose: "" | "INSTALLMENT" | "APPOINTMENT" | "COURSE";
  decision: "" | "APPROVED" | "REJECTED";
  studentName: string;
  verifierName: string;
  courseName: string;
  batchName: string;
  paymentMethod: "" | "bkash" | "bank";
  decidedFrom: string;
  decidedTo: string;
}

const PaymentHistory: React.FC = () => {
  const [data, setData] = useState<PaymentHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination (1-indexed like PendingPayments)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortBy] = useState("decidedAt");
  const [sortDirection] = useState<"ASC" | "DESC">("DESC");

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    purpose: "",
    decision: "",
    studentName: "",
    verifierName: "",
    courseName: "",
    batchName: "",
    paymentMethod: "",
    decidedFrom: "",
    decidedTo: "",
  });

  // Selected audit for details modal
  const [selectedAudit, setSelectedAudit] = useState<PaymentAuditDto | null>(
    null,
  );

  const [revertingId, setRevertingId] = useState<number | null>(null);
  const [confirmRevertId, setConfirmRevertId] = useState<number | null>(null);

  const handleRevertToReview = async () => {
    if (confirmRevertId === null) return;
    setRevertingId(confirmRevertId);
    setConfirmRevertId(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/installments/${confirmRevertId}/revert-to-review`,
        { method: "PUT", credentials: "include" },
      );
      if (!res.ok) throw new Error(await res.text());
      setSelectedAudit(null);
      await fetchHistory();
    } catch (err: any) {
      setError(err.message || "Failed to revert payment");
    } finally {
      setRevertingId(null);
    }
  };

  // Payment slip preview state
  const [loadingSlipUrl, setLoadingSlipUrl] = useState(false);
  const [slipPreviewUrl, setSlipPreviewUrl] = useState<string | null>(null);
  const [showSlipPreview, setShowSlipPreview] = useState(false);

  // helper
  const fetchSlipByPaymentId = async (paymentId: number) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/${paymentId}/slip`,
      { credentials: "include" },
    );
    const data = await res.json();
    if (!res.ok || !data.success || !data.hasSlip) {
      throw new Error(
        data.message || "No payment slip available for this payment",
      );
    }
    return data.downloadUrl as string;
  };

  const fetchSlipByAuditId = async (auditId: number) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/audits/${auditId}/slip`,
      { credentials: "include" },
    );
    const data = await res.json();
    if (!res.ok || !data.success || !data.hasSlip) {
      throw new Error(
        data.message || "No payment slip available for this audit",
      );
    }
    return data.downloadUrl as string;
  };

  const fetchSlipByInstallmentId = async (installmentId: number) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/installments/${installmentId}/slip`,
      { credentials: "include" },
    );
    const data = await res.json();
    if (!res.ok || !data.success || !data.hasSlip) {
      throw new Error(
        data.message || "No payment slip available for this installment",
      );
    }
    return data.downloadUrl as string;
  };

  const openSlipPreview = async (row: PaymentAuditDto) => {
    setLoadingSlipUrl(true);
    setError(null);
    try {
      let url: string | null = null;

      // 1) Try paymentId if present
      if (row.paymentId) {
        try {
          url = await fetchSlipByPaymentId(row.paymentId);
        } catch {}
      }

      // 2) Fallback: by audit id
      if (!url) {
        try {
          url = await fetchSlipByAuditId(row.id);
        } catch {}
      }

      // 3) Fallback: by installment id (latest payment of that installment)
      if (!url && row.installmentId) {
        try {
          url = await fetchSlipByInstallmentId(row.installmentId);
        } catch {}
      }

      if (!url)
        throw new Error(
          "Could not resolve a payment slip for this history row.",
        );

      setSlipPreviewUrl(url);
      setShowSlipPreview(true);
    } catch (e: any) {
      setError(e.message || "Failed to load payment slip");
    } finally {
      setLoadingSlipUrl(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        page: currentPage - 1, // Convert to 0-indexed for backend
        size: itemsPerPage,
        sortBy,
        sortDirection,
        // Convert empty strings to null
        purpose: filters.purpose || null,
        decision: filters.decision || null,
        studentName: filters.studentName || null,
        verifierName: filters.verifierName || null,
        courseName: filters.courseName || null,
        batchName: filters.batchName || null,
        paymentMethod: filters.paymentMethod || null,
        decidedFrom: filters.decidedFrom || null,
        decidedTo: filters.decidedTo || null,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/history`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch payment history");
      }

      const result: PaymentHistoryResponse = await response.json();
      setData(result);
    } catch (err: any) {
      setError(err.message || "Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchHistory();
    }
  }, [filters]);

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      purpose: "",
      decision: "",
      studentName: "",
      verifierName: "",
      courseName: "",
      batchName: "",
      paymentMethod: "",
      decidedFrom: "",
      decidedTo: "",
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return `৳${amount.toLocaleString()}`;
  };

  const getDecisionBadge = (decision: "APPROVED" | "REJECTED") => {
    if (decision === "APPROVED") {
      return (
        <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-1 rounded-full text-xs font-semibold">
          <FaCheckCircle />
          Approved
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs font-semibold">
        <FaTimesCircle />
        Rejected
      </span>
    );
  };

  const getPurposeBadge = (audit: PaymentAuditDto) => {
    const styles = {
      INSTALLMENT: "bg-blue-100 text-blue-800",
      APPOINTMENT: "bg-purple-100 text-purple-800",
      COURSE: "bg-green-100 text-green-800",
    };

    const displayText =
      audit.purpose === "INSTALLMENT" && audit.installmentSequence
        ? `INSTALLMENT #${audit.installmentSequence}`
        : audit.purpose;

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          styles[audit.purpose as keyof typeof styles] ||
          "bg-gray-100 text-gray-800"
        }`}
      >
        {displayText}
      </span>
    );
  };

  const getPaymentMethodIcon = (method: string) => {
    if (method?.toLowerCase().includes("bkash")) {
      return <FaMobileAlt className="text-pink-600" />;
    }
    if (method?.toLowerCase().includes("bank")) {
      return <FaUniversity className="text-blue-600" />;
    }
    return <FaQuestionCircle className="text-gray-400" />;
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment history...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-800">
          <FaTimesCircle />
          <span className="font-semibold">Error:</span> {error}
        </div>
      </div>
    );
  }

  const totalPages = data?.totalPages || 1;
  const hasData = data && data.content.length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FaHistory className="text-2xl text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Payment History
            </h2>
            <p className="text-sm text-gray-600">
              {data?.totalElements || 0} total records
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#007bb5] transition-colors"
        >
          <FaFilter />
          {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Purpose Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purpose
              </label>
              <select
                value={filters.purpose}
                onChange={(e) =>
                  handleFilterChange(
                    "purpose",
                    e.target.value as FilterState["purpose"],
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="INSTALLMENT">Installment</option>
                <option value="APPOINTMENT">Appointment</option>
                <option value="COURSE">Course</option>
              </select>
            </div>

            {/* Decision Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decision
              </label>
              <select
                value={filters.decision}
                onChange={(e) =>
                  handleFilterChange(
                    "decision",
                    e.target.value as FilterState["decision"],
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            {/* Payment Method Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                value={filters.paymentMethod}
                onChange={(e) =>
                  handleFilterChange(
                    "paymentMethod",
                    e.target.value as FilterState["paymentMethod"],
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All</option>
                <option value="bkash">bKash</option>
                <option value="bank">Bank</option>
              </select>
            </div>

            {/* Student Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Student Name
              </label>
              <input
                type="text"
                value={filters.studentName}
                onChange={(e) =>
                  handleFilterChange("studentName", e.target.value)
                }
                placeholder="Search by name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Verifier Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verifier Name
              </label>
              <input
                type="text"
                value={filters.verifierName}
                onChange={(e) =>
                  handleFilterChange("verifierName", e.target.value)
                }
                placeholder="Search by verifier..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Course Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Name
              </label>
              <input
                type="text"
                value={filters.courseName}
                onChange={(e) =>
                  handleFilterChange("courseName", e.target.value)
                }
                placeholder="Search by course..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Batch Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Batch Name
              </label>
              <input
                type="text"
                value={filters.batchName}
                onChange={(e) =>
                  handleFilterChange("batchName", e.target.value)
                }
                placeholder="Search by batch..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decided From
              </label>
              <input
                type="date"
                value={filters.decidedFrom}
                onChange={(e) =>
                  handleFilterChange("decidedFrom", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Date To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decided To
              </label>
              <input
                type="date"
                value={filters.decidedTo}
                onChange={(e) =>
                  handleFilterChange("decidedTo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              <FaBroom />
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Purpose
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Decision
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Decided At
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!hasData ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <FaSearch className="text-4xl text-gray-300" />
                      <p className="text-gray-500">No payment history found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                data.content.map((audit) => (
                  <tr
                    key={audit.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{audit.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {audit.studentName}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {audit.studentId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPurposeBadge(audit)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(audit.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getPaymentMethodIcon(audit.paymentMethod)}
                        <span className="text-sm text-gray-900 capitalize">
                          {audit.paymentMethod || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getDecisionBadge(audit.decision)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDateTime(audit.decidedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedAudit(audit)}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-[#009fe3] text-white rounded-lg hover:bg-[#007bb5] transition-colors text-sm"
                        >
                          <FaInfoCircle />
                          Details
                        </button>
                        {audit.purpose === "INSTALLMENT" && audit.installmentId && (
                          <button
                            onClick={() => setConfirmRevertId(audit.installmentId!)}
                            disabled={revertingId === audit.installmentId}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors text-sm"
                            title="Revert to In Review"
                          >
                            {revertingId === audit.installmentId ? (
                              <FaSpinner className="animate-spin" />
                            ) : (
                              <FaUndo />
                            )}
                            Revert
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {hasData && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>

            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages} ({data.totalElements} total)
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiChevronLeft />
              </button>
              <span className="text-sm font-medium text-gray-700">
                {currentPage}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {selectedAudit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-[#009fe3] p-6 rounded-t-xl">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    Payment Audit Details
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">
                    Audit ID: #{selectedAudit.id}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedAudit(null)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Decision Status */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border-l-4 border-[#009fe3]">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-1">
                      Decision Status
                    </h4>
                    {getDecisionBadge(selectedAudit.decision)}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-600 mb-1">Decided At</div>
                    <div className="text-sm font-medium text-gray-900">
                      {formatDateTime(selectedAudit.decidedAt)}
                    </div>
                  </div>
                </div>
                {selectedAudit.decisionReason && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-600 mb-1">Reason</div>
                    <div className="text-sm text-gray-900">
                      {selectedAudit.decisionReason}
                    </div>
                  </div>
                )}
              </div>

              {/* Student Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-1 h-4 bg-[#009fe3] rounded"></span>
                  Student Information
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Name:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedAudit.studentName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Email:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedAudit.studentEmail}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Student ID:</span>
                    <span className="text-sm font-medium text-gray-900">
                      #{selectedAudit.studentId}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-1 h-4 bg-green-600 rounded"></span>
                  Payment Information
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Purpose:</span>
                    {getPurposeBadge(selectedAudit)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Amount:</span>
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(selectedAudit.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Method:</span>
                    <div className="flex items-center gap-2">
                      {getPaymentMethodIcon(selectedAudit.paymentMethod)}
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {selectedAudit.paymentMethod || "Unknown"}
                      </span>
                    </div>
                  </div>
                  {selectedAudit.paymentDate && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">
                        Payment Date:
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(selectedAudit.paymentDate)}
                      </span>
                    </div>
                  )}
                  {selectedAudit.submittedAt && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">
                        Submitted At:
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDateTime(selectedAudit.submittedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Details */}
              {(selectedAudit.transactionId ||
                selectedAudit.bankName ||
                selectedAudit.branchName ||
                selectedAudit.referenceNumber) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-purple-600 rounded"></span>
                    Payment Details
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {selectedAudit.transactionId && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">
                          Transaction ID:
                        </span>
                        <span className="text-sm font-mono font-medium text-gray-900 bg-white px-2 py-1 rounded border">
                          {selectedAudit.transactionId}
                        </span>
                      </div>
                    )}
                    {selectedAudit.bankName && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Bank:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedAudit.bankName}
                        </span>
                      </div>
                    )}
                    {selectedAudit.branchName && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Branch:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedAudit.branchName}
                        </span>
                      </div>
                    )}
                    {selectedAudit.referenceNumber && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">
                          Reference:
                        </span>
                        <span className="text-sm font-mono font-medium text-gray-900 bg-white px-2 py-1 rounded border">
                          {selectedAudit.referenceNumber}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Course/Installment Info */}
              {(selectedAudit.courseName ||
                selectedAudit.batchName ||
                selectedAudit.installmentSequence) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-indigo-600 rounded"></span>
                    Course Information
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {selectedAudit.courseName && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Course:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedAudit.courseName}
                        </span>
                      </div>
                    )}
                    {selectedAudit.batchName && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Batch:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedAudit.batchName}
                        </span>
                      </div>
                    )}
                    {selectedAudit.installmentSequence && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">
                          Installment:
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          #{selectedAudit.installmentSequence}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Verifier Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-1 h-4 bg-amber-600 rounded"></span>
                  Verified By
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Name:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedAudit.verifierName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Email:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedAudit.verifierEmail}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Role:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedAudit.verifierRole}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Verifier ID:</span>
                    <span className="text-sm font-medium text-gray-900">
                      #{selectedAudit.verifierId}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Slip Preview Button */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-1 h-4 bg-indigo-600 rounded"></span>
                  Payment Slip
                </h4>

                {/* Error Message */}
                {error && error.toLowerCase().includes("slip") && (
                  <div className="mb-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FaInfoCircle className="text-yellow-600" />
                      <p className="text-sm text-yellow-800">{error}</p>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() =>
                    selectedAudit && openSlipPreview(selectedAudit)
                  }
                  disabled={loadingSlipUrl}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#009fe3] text-white rounded-lg hover:bg-[#0088cc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingSlipUrl ? (
                    <>
                      <FaSpinner className="animate-spin" /> Loading...
                    </>
                  ) : (
                    <>
                      <FaEye /> View Payment Slip
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 rounded-b-xl flex gap-2">
              {selectedAudit.purpose === "INSTALLMENT" && selectedAudit.installmentId && (
                <button
                  onClick={() => setConfirmRevertId(selectedAudit.installmentId!)}
                  disabled={revertingId === selectedAudit.installmentId}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
                >
                  {revertingId === selectedAudit.installmentId ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaUndo />
                  )}
                  Revert to Review
                </button>
              )}
              <button
                onClick={() => setSelectedAudit(null)}
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revert Confirmation Modal */}
      {confirmRevertId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl overflow-hidden">
            <div className="h-1.5 w-full bg-amber-500" />
            <div className="p-6">
              <h4 className="text-lg font-semibold text-gray-900">Confirm Revert</h4>
              <p className="mt-2 text-sm text-gray-600">
                This will revert the payment back to <b>In Review</b>, remove it from history, and deduct the amount from total revenue. This cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setConfirmRevertId(null)}
                  className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevertToReview}
                  className="px-5 py-2 rounded-lg text-white text-sm font-medium bg-amber-500 hover:bg-amber-600"
                >
                  Revert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Slip Preview Modal */}
      {showSlipPreview && slipPreviewUrl && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="min-h-full flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90dvh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#009fe3] to-[#0088cc] p-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Payment Slip</h3>
                <button
                  onClick={() => {
                    setShowSlipPreview(false);
                    setSlipPreviewUrl(null);
                  }}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-auto p-6">
                <div className="bg-gray-50 rounded-lg border overflow-hidden">
                  <iframe
                    src={slipPreviewUrl}
                    title="Payment Slip"
                    className="w-full h-[60dvh]"
                  />
                </div>
                {error && (
                  <p className="text-sm text-yellow-700 bg-yellow-50 border-l-4 border-yellow-400 px-3 py-2 mt-3 rounded">
                    {error}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  If the preview doesn't load, use "Open in New Tab".
                </p>
                <div className="pb-6" />
              </div>

              {/* Non-scrolling footer */}
              <div className="border-t bg-gray-50 p-4 flex gap-3 pb-[env(safe-area-inset-bottom)]">
                <a
                  href={slipPreviewUrl}
                  download
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FaDownload /> Download
                </a>
                <a
                  href={slipPreviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#0088cc] transition-colors"
                >
                  <FaEye /> Open in New Tab
                </a>
                <button
                  onClick={() => {
                    setShowSlipPreview(false);
                    setSlipPreviewUrl(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
